import { FC, useEffect, useState, useMemo, ChangeEvent, useRef } from 'react';
import { Column, useTable } from 'react-table';
import { User } from '../../../models/user';
import AdminTeamService from '../../../services/admin/adminTeam'
import FixtureService from '../../../services/fixture'
import HelperService from '../../../services/helper'
import useNotyf from '../../../context/useNotify';
import { League } from '../../../models/league';
import Dropdown from '../../common/Dropdown';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faEdit, faEraser, faExchangeAlt, faUserTie } from "@fortawesome/free-solid-svg-icons";
import ReactPaginate from 'react-paginate';
import { Meta, QueryParams } from '../../../models/misc';
import { StyledPagination, StyledTable } from '.././Styles';
import { Fixture, FixtureUpdateDTO } from '../../../models/fixture';
import ReactModal from '../../common/ReactModal';
import AdminFixtureService from "../../../services/admin/adminFixture"
import TeamService from "../../../services/team"
import LeagueService from "../../../services/league"
import adminLeagueService from '../../../services/admin/adminLeague';
import { Team, TeamSingle } from '../../../models/team';
import ConfirmationModalContent from '../../common/ConfirmationModalContent';

interface Props {
  league: League
  refresh: () => void
}

const UserTable: FC<Props> = ({ league, refresh }) => {
  const notyf = useNotyf()
    
    // Data
  const [team, setTeam] = useState<TeamSingle>();
  const teamRef = useRef<TeamSingle>();


  const teamOptions = useMemo(() => {
    if (!league) {
      return [];
    }
    return league.teams.map(team => ({ label: team.name, value: team }));
  }, [league]);
  
  // API
  const fetchTeam = async (id: string) => {
    try {
        const team = await TeamService.getTeamById(id)
        setTeam(team)
    } catch (err) {
      console.log("Error fetching league: ", err)
      HelperService.showNotfyErr(notyf, err, "Error fetching league.")
    }
  }

  const releasePlayer = async (userId: string) => {
    if (!teamRef.current) {
        return;
    }
    try {
        await AdminTeamService.releasePlayer(teamRef.current._id, userId);
        notyf.success("Player Released.");
        fetchTeam(team?._id || "")
    } catch (err) {
        console.log("Error releasing player: ", err);
        HelperService.showNotfyErr(notyf, err, "Error releasing player.");
    }
  };

  const clearStats = async (userId: string) => {
    if (!teamRef.current) {
        return;
    }
    try {
        await adminLeagueService.clearPlayerStats(league._id, userId);
        notyf.success("Stats Cleared.");
        fetchTeam(team?._id   || "")
    } catch (err) {
        console.log("Error clearing stats: ", err);
        HelperService.showNotfyErr(notyf, err, "Error clearing stats.");
    }
  }

  const updateTeam = async (managerId: string) => {
      if (!team) {
          return;
      }
    try {
      await AdminTeamService.switchManager(team?._id, managerId);
      notyf.success("Team updated.")
  } catch (err) {
      console.log("Error updating team: ", err);
      HelperService.showNotfyErr(notyf, err, "Error updating team.");
  }
};


  // Methods
  const handleSelectTeam = (teamOption: any) => {
    if (!teamOption) {
        return;
    }
    teamRef.current = teamOption.value;
    fetchTeam(teamOption.value._id);
};

  const handleClearStats = (id: string) => {
    clearStats(id)
  };
  
  const handleReleasePlayer = (id: string) => {
    releasePlayer(id)
  };  

  const handleSwitchManager = async (newManagerId: string) => {
    await updateTeam(newManagerId);
    if (!team) {
        return;
    }
    fetchTeam(team._id);
  };

  // Hooks
  useEffect(() => {
    teamRef.current = undefined;
}, [league]);

    // Table data
    const columns: Column<User>[] = useMemo(
        () => [
          {
            Header: 'Username',
            accessor: 'username',
            Cell: ({ row }) => (
              <div className="d-flex align-items-center">
                <img
                  src={row.original.profileImage}
                  alt={row.original.username}
                  className="img-fluid me-2" // Bootstrap classes for responsive image and margin
                  style={{ maxWidth: '30px', maxHeight: '30px', borderRadius: '50%' }} // Adjust size and make it circular
                />
                {row.original.username}
              </div>
            ),
          },
          {
            Header: 'Twitch Link',
            accessor: 'twitch',
          },
          {
            Header: 'YouTube Link',
            accessor: 'youtube',
          },
          {
            Header: 'Position',
            accessor: 'position',
          },
          {
            Header: 'Actions',
            accessor: (user: User) => user,
            Cell: ({ cell: { value: user } }: { cell: { value: User } }) => (
              <div className="actions d-flex">
                {team?.managerId._id !== user._id ? (
                  <button
                    className='btn btn-warning btn-sm me-2'
                    onClick={() => handleSwitchManager(user._id)}
                    aria-label="Make Manager"
                    title="Assign as manager"
                  >
                    <FontAwesomeIcon icon={faExchangeAlt} /> Manager
                  </button>)
                  :
                  (<button
                    className='btn btn-primary btn-sm me-2'
                    disabled
                    title="Current Manager"
                    aria-label="Current Manager"
                  >
                    <FontAwesomeIcon icon={faUserTie} /> Manager
                </button>)}
                <button className='btn btn-warning btn-sm me-2' onClick={() => handleShowClearStatsModal(user)}>
                  <FontAwesomeIcon icon={faEraser} /> Clear Stats
                </button>
                <button className='btn btn-danger btn-sm' onClick={() => handleShowReleasePlayerModal(user)}>
                  <FontAwesomeIcon icon={faTrashAlt} /> Release Player
                </button>
              </div>
            ),
            className: 'actions',
            width: 150
          },
        ],
        [team]
      );
      const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
      } = useTable({ columns, data: team?.roster || [] });
  
  // Modals
  const [selectedUser, setSelectedUser] = useState<User | null>();

  const closeModals = () => {
    setSelectedUser(null);
    setShowClearStatsModal(false);
    setShowReleasePlayerModal(false);
  }

  // Modal - Clear stats
  const [showClearStatsModal, setShowClearStatsModal] = useState(false);
  
  const handleShowClearStatsModal = (user: User) => {
    setSelectedUser(user);
    setShowClearStatsModal(true);
  }

  const handleClickClearStats = async () => {
    if (selectedUser) {
      await clearStats(selectedUser._id);
    }
    closeModals();
  }

  // Modal - Release player
  const [showReleasePlayerModal, setShowReleasePlayerModal] = useState(false);

  const handleShowReleasePlayerModal = (user: User) => {
    setSelectedUser(user);
    setShowReleasePlayerModal(true);
  }

  const handleClickReleasePlayer = async () => {
    if (selectedUser) {
      await releasePlayer(selectedUser._id);
    }
    closeModals();
  }

  return (
      <>
      {/* team info and filters */}
      <div className='d-flex justify-content-end align-items-end px-2 flex-wrap my-2'>
          <div className="d-flex flex-wrap gap-3 px-3">
            <div>
              Players: <strong className="text-info">{team?.roster.length ?? "Not Selected"}</strong>
          </div>  
          <div>
              Manager: <strong className="text-info">{team?.manager ?? "Not Selected"}</strong>
          </div> 
          <div>
              Team: <strong className="text-info">{team?.name ?? "Not Selected"}</strong>
          </div>
          </div>
          {league ?
            <Dropdown
              options={teamOptions}
              onSelect={handleSelectTeam}
              text="Select Team"
            />
          : null}
      </div>

        {/* table */}
        <div className='table-responsive p-2'>
          <StyledTable {...getTableProps()}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
      </div>
      
      {/* Clear Stats Modal */}
      <ReactModal
        modalOpen={showClearStatsModal}
        component={<ConfirmationModalContent
          onConfirm={handleClickClearStats}
          onCancel={() => closeModals()}
          message={`Are you sure you want to clear stats for ${selectedUser?.username}?`}
        />}
        closeModal={() => closeModals()}
      />

      {/* Release Player Modal */}
      <ReactModal
        modalOpen={showReleasePlayerModal}
        component={<ConfirmationModalContent
          onConfirm={handleClickReleasePlayer}
          onCancel={() => closeModals()}
          message={`Are you sure you want to release ${selectedUser?.username}?`}
        />}
        closeModal={() => closeModals()}
      />
        
    </>
  );
};

export default UserTable;
